import type { AssessmentTab } from "../../../server/modules/homecare/assessments";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { listLink } from "../../ui/list-link";
import { openModal } from "../../ui/modal";
import { area, field, refuse, select } from "../clients/forms";

// Add appointment (brief 1E-28; mockup v86 apModal): one form for appointments everywhere, from
// the Assessment tab and from Next steps on Patient and payer (Thomas, PR #59). Its kind from the
// company's list, with whom from the care team (or someone new, or no one for an outing), the
// day, time and Back at, how it repeats (until a date), the ride, the aide during it, and notes.

type Tab = AssessmentTab;
type Res = { ok: boolean; status: number; body: Tab & { error?: string; note?: string | null } };

/** What the form offers, as the Assessment tab has it. */
export type AppointmentChoices = Pick<Tab, "today" | "careTeam" | "repeats" | "rides" | "aides"> & {
  choices: Pick<Tab["choices"], "appointmentKinds">;
};

/**
 * Opens the form for a patient with the choices in hand. `saved` gets the Assessment tab as the
 * server answers it after saving.
 */
export function appointmentForm(
  patientId: string,
  data: AppointmentChoices,
  saved: (res: Res) => unknown,
) {
  const base = `/api/patients/${encodeURIComponent(patientId)}`;
  const kind = select(
    "Kind",
    data.choices.appointmentKinds.map((k) => [k, k] as const),
    data.choices.appointmentKinds[0] ?? "",
  );
  const team = data.careTeam.map(
    (c) => [c.id, c.organization ? `${c.name} · ${c.organization}` : c.name] as const,
  );
  const withSel = select(
    "With",
    [...team, ["new", "Someone new"], ["none", "No one (an outing)"]] as const,
    team[0]?.[0] ?? "new",
  );
  const who = field("Name", "", {
    maxlength: "120",
    placeholder: "e.g. Dr. Lena Osei, cardiology",
  });
  who.el.hidden = withSel.input.value !== "new";
  withSel.input.addEventListener("change", () => {
    who.el.hidden = withSel.input.value !== "new";
  });
  const day = field("Date", data.today, { type: "date" });
  const time = field("Time", "09:00", { type: "time" });
  const back = field("Back at", "", { type: "time" });
  const repeats = select(
    "Repeats",
    data.repeats.map((r) => [r.value, r.label] as const),
    "once",
  );
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dayBoxes = dayNames.map((d, i) =>
    el("label", { class: "tsr" }, el("input", { type: "checkbox", value: String(i) }), ` ${d}`),
  );
  const every = field("Every how many weeks", "2", { type: "number", min: "1", max: "12" });
  const until = field("Until", "", { type: "date" });
  const days = el(
    "fieldset",
    { class: "tsf" },
    el("legend", {}, "On"),
    el("div", { class: "tsgrid" }, ...dayBoxes),
  );
  const repeatBox = el(
    "div",
    { hidden: true },
    days,
    el("div", { class: "formgrid" }, every.el, until.el),
  );
  repeats.input.addEventListener("change", () => {
    const r = repeats.input.value;
    repeatBox.hidden = r === "once";
    days.hidden = r === "month";
    every.el.hidden = r !== "weeks";
    // The first day's weekday is ticked to start.
    if (r !== "once" && r !== "month" && !dayBoxes.some((b) => b.querySelector("input")?.checked)) {
      const wd = day.input.value ? new Date(`${day.input.value}T12:00:00Z`).getUTCDay() : 1;
      const box = dayBoxes[wd]?.querySelector("input");
      if (box) box.checked = true;
    }
  });
  const ride = select(
    "Ride",
    data.rides.map((r) => [r.value, r.label] as const),
    "not_needed",
  );
  const aide = select(
    "Aide during it",
    data.aides.map((r) => [r.value, r.label] as const),
    "not_during",
  );
  const notes = area("Notes", "");
  notes.input.placeholder = "e.g. Bring the medication list";
  const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
  save.addEventListener("click", async () => {
    const w = withSel.input.value;
    const r = repeats.input.value;
    save.disabled = true;
    const res = (await api<Tab>("POST", `${base}/appointments`, {
      kind: kind.input.value,
      ...(w === "new"
        ? { withWhom: who.input.value }
        : w === "none"
          ? { withWhom: kind.input.value }
          : { careTeamId: w }),
      day: day.input.value || null,
      time: time.input.value || null,
      backTime: back.input.value,
      repeats: r,
      ...(r === "once"
        ? {}
        : {
            days: dayBoxes
              .map((b) => b.querySelector("input") as HTMLInputElement)
              .filter((i) => i.checked)
              .map((i) => Number(i.value)),
            every: r === "weeks" ? Number(every.input.value) : 1,
            until: until.input.value || null,
          }),
      ride: ride.input.value,
      aide: aide.input.value,
      notes: notes.input.value,
    })) as Res;
    save.disabled = false;
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    await saved(res);
  });
  const m = openModal({
    title: "Add appointment",
    wide: true,
    body: [
      el(
        "div",
        { class: "form", style: "max-width:none" },
        el("div", { class: "formgrid" }, kind.el, withSel.el, who.el),
        listLink("appointment_kind"),
        el("div", { class: "formgrid" }, day.el, time.el, back.el, repeats.el),
        repeatBox,
        el("div", { class: "formgrid" }, ride.el, aide.el),
        notes.el,
      ),
    ],
    actions: [save],
  });
}

/** Opens the form from anywhere else (Next steps): it reads the patient's choices first. */
export async function openAppointmentForm(patientId: string, saved: (res: Res) => unknown) {
  const res = (await api<Tab>(
    "GET",
    `/api/patients/${encodeURIComponent(patientId)}/assessment`,
  )) as Res;
  if (!res.ok) return toast("The appointment can't be added", res.body.error);
  appointmentForm(patientId, res.body, saved);
}
